
import React, { useState } from "react";
import { 
  Eye, 
  EyeOff, 
  Type, 
  Minimize2, 
  Headphones, 
  RotateCcw 
} from "lucide-react";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const AccessibilityPanel = () => {
  const { settings, updateSetting, resetSettings } = useAccessibility();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-12 w-12 shadow-lg bg-background"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Accessibility options"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8" />
                <path d="M8 12h8" />
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Accessibility Options</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleContent className="bg-card border rounded-lg shadow-lg p-4 mt-2 w-72 absolute bottom-14 right-0">
          <h3 className="text-lg font-semibold mb-4">Accessibility Options</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <EyeOff className="h-5 w-5" />
                <Label htmlFor="high-contrast">High Contrast</Label>
              </div>
              <Switch
                id="high-contrast"
                checked={settings.highContrast}
                onCheckedChange={(checked) => updateSetting("highContrast", checked)}
                aria-label="Toggle high contrast mode"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Type className="h-5 w-5" />
                <Label htmlFor="large-text">Large Text</Label>
              </div>
              <Switch
                id="large-text"
                checked={settings.largeText}
                onCheckedChange={(checked) => updateSetting("largeText", checked)}
                aria-label="Toggle large text mode"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Minimize2 className="h-5 w-5" />
                <Label htmlFor="reduced-motion">Reduced Motion</Label>
              </div>
              <Switch
                id="reduced-motion"
                checked={settings.reducedMotion}
                onCheckedChange={(checked) => updateSetting("reducedMotion", checked)}
                aria-label="Toggle reduced motion"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Headphones className="h-5 w-5" />
                <Label htmlFor="screen-reader">Screen Reader Optimized</Label>
              </div>
              <Switch
                id="screen-reader"
                checked={settings.screenReader}
                onCheckedChange={(checked) => updateSetting("screenReader", checked)}
                aria-label="Toggle screen reader optimizations"
              />
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2" 
              onClick={resetSettings}
              aria-label="Reset all accessibility settings"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Settings
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default AccessibilityPanel;
